/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect } from "react";
import { Hourglass } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../components/ui/Button";
import { Logo } from "../components/ui/Logo";
import { Spinner } from "../components/ui/Spinner";

const RECHECK_MILLIS = 20_000;

/**
 * Where a signed-in account with no role lands: typically a teammate's first Google sign-in.
 * The screen upgrades itself: it rechecks access on an interval and on window focus, and the
 * moment an admin grants a role the session rotates and the console opens, no sign-out needed.
 */
export function PendingAccess() {
  const { session, logout, recheckAccess } = useAuth();
  const email = session.state === "authenticated" ? session.user.email : "";

  useEffect(() => {
    const check = () => {
      void recheckAccess();
    };
    check();
    const interval = setInterval(check, RECHECK_MILLIS);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", check);
    };
  }, [recheckAccess]);

  return (
    <div className="grid min-h-dvh place-items-center bg-bg-base p-6">
      <div className="enter w-full max-w-md text-center">
        <Logo variant="type" className="mx-auto h-6 text-text-primary" />
        <div className="border border-border bg-bg-elevated shadow-card mt-8 rounded-3xl p-8">
          <div className="mx-auto grid size-12 place-items-center rounded-2xl bg-accent/10 text-accent">
            <Hourglass size={22} />
          </div>
          <h1 className="mt-5 font-display text-xl text-text-primary">
            Your account is waiting for access
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-text-secondary">
            You are signed in as <span className="font-medium text-text-primary">{email}</span>,
            but no role has been assigned yet. Ask an admin to grant your role and this screen
            opens the console on its own.
          </p>
          <p className="mt-5 flex items-center justify-center gap-2 text-xs text-text-tertiary">
            <Spinner size="xs" />
            Checking automatically
          </p>
          <div className="mt-8">
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                void logout();
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
